import RouteConstants from '@/features/router/RouteConstants';
import { PageHeader, PageLayout } from '@/global/components';
import { BreadcrumbItem } from '@/lib/Breadcrumbs/types';
import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { BaseAudience } from '@/models/audiences';
import { DistributionChannelContent } from '@/models/distributionChannels';
import { CyclrTemplate } from '@/services/endpoints/integrations';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CREATE_DESTINATION_STEPS } from '../constants';
import {
    AudienceAndDestinationStep,
    AudienceAndDestinationSyncReviewStep,
    DestinationSelectPropertiesStep,
} from './steps';

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'ALL:destinations.title',
    path: RouteConstants.destinations,
  },
];

export const CreateDestinationContent: FC = () => {
  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep } = useCreateStepper(
    CREATE_DESTINATION_STEPS
  );

  const [distributionChannelContent, setDistributionChannelContent] = useState<DistributionChannelContent | undefined>();
  const [selectedTemplate, setSelectedTemplate] = useState<CyclrTemplate>();
  const [selectedAudience, setSelectedAudience] = useState<BaseAudience>();

  // this is needed because it allow back from sync to selectProperties step
  // otherwise we would not be able to show all integration fields if unchecked
  const [initialDistributionChannelContent, setInitialDistributionChannelContent] = useState<DistributionChannelContent>(distributionChannelContent);

  // Dont include distributionChannelContent in dependency array
  // because we dont want initialDistributionChannelContent to be updated when it change
  // but only when connector change
  useEffect(() => {
    setInitialDistributionChannelContent(distributionChannelContent);
  }, [selectedTemplate]);

  const completeAndGoNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const goToSelectPropertiesStep = (audience: BaseAudience, template: CyclrTemplate, _distributionChannelContent: DistributionChannelContent) => {
    setSelectedAudience(audience);
    setSelectedTemplate(template);
    setDistributionChannelContent(_distributionChannelContent);
    setInitialDistributionChannelContent(_distributionChannelContent);

    completeAndGoNextStep();
  };

  const history = useHistory();
  const goToDestinations = () => {
    history.push(RouteConstants.destinations);
  };

  const handleCancel = () => {
    goToDestinations();
  };

  const handleGoPreviousStep = () => {
    setDistributionChannelContent(undefined);
    goToPreviousStep();
  };

  const handleGoBackToSelectPropertiesStep = () => {
    // reset the distribution channel content so that we can show every field again
    // otherwise if user deselect one or more fields (checkbox) we wont be able to show that cause
    // backend return the object without those fields.
    setDistributionChannelContent(initialDistributionChannelContent);
    goToPreviousStep();
  };

  const goToSyncStep = (_distributionChannelContent: DistributionChannelContent) => {
    setDistributionChannelContent(_distributionChannelContent);
    completeAndGoNextStep();
  };


  const stepTemplate = () => {
    switch (currentStepId) {
      case 'selectAudienceAndDestination':
        return (
          <AudienceAndDestinationStep
            nextStep={goToSelectPropertiesStep}
            onCancel={handleCancel}
            distributionChannelContent={distributionChannelContent}
            forceAudience={selectedAudience}
            forceTemplate={selectedTemplate}
          />
        );
      
      case 'selectProperties':
        return (
          <DestinationSelectPropertiesStep
            distributionChannelContent={distributionChannelContent}
            selectedTemplate={selectedTemplate}
            selectedAudience={selectedAudience}
            nextStep={goToSyncStep}
            prevStep={handleGoPreviousStep}
            onCancel={handleCancel}
          />
        );
      
      case 'sync':
        return (
          <AudienceAndDestinationSyncReviewStep
            selectedAudience={selectedAudience}
            selectedTemplate={selectedTemplate}
            distributionChannelContent={distributionChannelContent}
            prevStep={handleGoBackToSelectPropertiesStep}
            onSyncSuccessful={completeCurrentStep}
            onCancel={handleCancel}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <PageHeader breadcrumbs={breadcrumbs} title='ALL:destinations.title' subtitle={'ALL:destinations.createSubtitle'} />

      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />

      {stepTemplate()}
    </PageLayout>
  );
};
